import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { Menu, X, Zap } from "lucide-react";
import { useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/AuthProvider";
import { useCompany } from "@/lib/CompanyProvider";
import { useTenantFeature } from "@/hooks/use-tenant-feature";
import { Sidebar } from "@/components/Sidebar";

export const Route = createFileRoute("/app")({
  beforeLoad: ({ context }) => {
    if (context.auth && !context.auth.isLoading) {
      if (!context.auth.user) {
        throw redirect({ to: "/login" });
      } else if (context.auth.hasOnboarded === null) {
        throw redirect({ to: "/" });
      } else if (context.auth.hasOnboarded === false) {
        throw redirect({ to: "/onboarding" });
      }
    }
  },
  component: AppLayout,
});

function AppLayout() {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const { hasAccess: hasPublicidade } = useTenantFeature("publicidade_tela");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isDemo = user?.email === "demo@autowash.com";

  const { data: subscription } = useQuery({
    queryKey: ["active-tenant-subscription", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return null;

      const { data: subData } = await supabase
        .from("company_subscriptions")
        .select("*, system_plans(name)")
        .eq("company_id", activeCompanyId)
        .maybeSingle();

      const { data: settingsData } = await supabase
        .from("company_settings")
        .select("subscription_expires_at, subscription_status, plan_id")
        .eq("id", activeCompanyId)
        .maybeSingle();

      return {
        ...(subData || {}),
        system_plans: subData?.system_plans,
        current_period_end:
          settingsData?.subscription_expires_at || subData?.current_period_end,
        status:
          settingsData?.subscription_status || subData?.status || "ACTIVE",
        plan_id: settingsData?.plan_id || subData?.plan_id,
      };
    },
    enabled: !!activeCompanyId,
  });

  return (
    <div className="flex flex-col lg:flex-row h-screen w-full bg-slate-950 text-slate-100 font-sans overflow-hidden">
      {/* Mobile Topbar */}
      <div className="flex items-center justify-between p-3.5 bg-slate-900 border-b border-slate-800 lg:hidden z-30 shrink-0">
        <div className="flex items-center gap-2">
          <img src="/logo.webp" className="w-7 h-7 object-contain" alt="Logo" />
          <span className="font-bold text-lg bg-gradient-to-r from-blue-400 to-blue-200 bg-clip-text text-transparent">
            AutoWash Pro
          </span>
        </div>
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
          aria-label="Toggle menu"
        >
          {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-40 lg:hidden transition-opacity"
        />
      )}

      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        hasPublicidade={hasPublicidade}
        subscription={subscription}
      />

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 bg-slate-950 overflow-hidden">
        {isDemo && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium shrink-0">
            <Zap size={16} className="fill-white" />
            Você está no Sandbox (Modo Demonstração). Os dados são reiniciados
            periodicamente.
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = "/";
              }}
              className="ml-2 underline font-bold hover:text-blue-200"
            >
              Sair do Sandbox
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto min-w-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
